import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Card, Spinner, Alert, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import {
  fetchCategories,
  addCategory,
  updateCategory,
  deleteCategory
} from '../../redux/actions/categoryActions';
import Pagination from '../common/Pagination';
import useDebounce from '../../hooks/useDebounce';
import { hasEditPermission, hasDeletePermission } from '../../utils/authConfig';
import CategoryForm from './CategoryForm';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardTitle,
  MobileCardRow,
  MobileCardFooter,
} from '../common/ListMobileLayout';

const CategoryList = () => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  // Check permissions
  const canEdit = hasEditPermission();
  const canDelete = hasDeletePermission();

  // Debounce the search query with a 500ms delay
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { items: categories, loading, total, error } = useSelector((state) => ({
    items: state.categories.items || [],
    loading: state.categories.loading,
    total: state.categories.total || 0,
    error: state.categories.error
  }));

  useEffect(() => {
    const params = {
      page: currentPage,
      items_per_page: pageSize
    };

    if (debouncedSearchQuery) {
      params.q = debouncedSearchQuery;
    }

    dispatch(fetchCategories(params));
  }, [dispatch, currentPage, pageSize, debouncedSearchQuery]);

  const handleShowModal = (category = null) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  const handleSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (editingCategory) {
        await dispatch(updateCategory(editingCategory.id, formData));
      } else {
        await dispatch(addCategory(formData));
      }
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save category:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await dispatch(deleteCategory(id));
      } catch (error) {
        console.error('Failed to delete category:', error);
      }
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  if (error) {
    return (
      <Alert variant="danger" className="mt-3">
        {error}
      </Alert>
    );
  }

  return (
    <div className="dashboard-list-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Categories</h2>
        {canEdit && (
          <Button variant="primary" onClick={() => handleShowModal()}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Category
          </Button>
        )}
      </div>

      <Card>
        <Card.Body>
          <div className="d-flex gap-3 mb-4">
            <div className="flex-grow-1">
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faSearch} />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search by name..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
          </div>

          <div className="list-pagination-top">
            <Pagination
              currentPage={currentPage}
              totalItems={total}
              itemsPerPage={pageSize}
              onPageChange={handlePageChange}
              position="top"
            />
          </div>

          <ListDesktop>
            <Table hover className="mb-0">
              <thead>
                                  <tr>
                    <th className="table-col-id">ID</th>
                    <th className="table-col-category-image">Thumbnail</th>
                    <th className="table-col-category-name">Name</th>
                    <th className="table-col-category-description">Description</th>
                    <th className="table-col-category-models">Models</th>
                    <th className="table-col-actions">Actions</th>
                  </tr>
                </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      <div className="loading-spinner">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      </div>
                    </td>
                  </tr>
                ) : (
                  categories.map((category) => (
                    <tr key={category.id}>
                      <td className="table-col-id">{category.id}</td>
                      <td className="table-col-category-image">
                        {category.category_thumbnail ? (
                          <img 
                            src={category.category_thumbnail} 
                            alt={category.category_name} 
                            className="table-image" 
                            style={{ maxWidth: '50px', maxHeight: '50px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="text-muted">No image</div>
                        )}
                      </td>
                      <td className="table-col-category-name">{category.category_name}</td>
                      <td className="table-col-category-description">
                        {category.category_description ? (
                          <div className="text-truncate" style={{ maxWidth: '200px' }} title={category.category_description}>
                            {category.category_description}
                          </div>
                        ) : (
                          <span className="text-muted">No description</span>
                        )}
                      </td>
                      <td className="table-col-category-models">
                        {category.models.map((model) => (
                          <span key={model.id} className="badge bg-primary me-2">{model.model_name}</span>
                        ))}
                      </td>
                      <td className="table-col-actions">
                        {canEdit && (
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-2"
                            onClick={() => handleShowModal(category)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                        )}
                        {canDelete && (
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDelete(category.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </ListDesktop>

          {!loading && (
            <ListMobile>
              {categories.map((category) => (
                <MobileCard key={category.id}>
                  <MobileCardTop>
                    {category.category_thumbnail ? (
                      <img
                        src={category.category_thumbnail}
                        alt={category.category_name}
                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                      />
                    ) : null}
                    <MobileCardTitle>{category.category_name}</MobileCardTitle>
                  </MobileCardTop>
                  <MobileCardRow label="ID">{category.id}</MobileCardRow>
                  <MobileCardRow label="Description">
                    {category.category_description || (
                      <span className="text-muted">No description</span>
                    )}
                  </MobileCardRow>
                  <MobileCardRow label="Models">
                    {category.models?.length
                      ? category.models.map((model) => (
                          <span key={model.id} className="badge bg-primary me-1 mb-1">
                            {model.model_name}
                          </span>
                        ))
                      : '—'}
                  </MobileCardRow>
                  {(canEdit || canDelete) && (
                    <MobileCardFooter>
                      {canEdit && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleShowModal(category)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(category.id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      )}
                    </MobileCardFooter>
                  )}
                </MobileCard>
              ))}
            </ListMobile>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={total}
            itemsPerPage={pageSize}
            onPageChange={handlePageChange}
            position="bottom"
          />
        </Card.Body>
      </Card>

      {canEdit && (
        <CategoryForm
          show={showModal}
          onHide={handleCloseModal}
          onSubmit={handleSubmit}
          editingCategory={editingCategory}
          loading={formLoading}
        />
      )}
    </div>
  );
};

export default CategoryList;